import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Package, Layers, PackageCheck, PackageX, AlertTriangle, DollarSign, Lock, Boxes } from "lucide-react";
import { StatusPill, fmtDate } from "./InventoryShared";

export const InventoryDashboard = ({ onViewInventory }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/inventory/summary").then((r) => setS(r.data)); }, []);
  const cards = [
    { icon: Package, label: "Total products", value: s?.total_products },
    { icon: Boxes, label: "Total units", value: s?.total_units },
    { icon: PackageCheck, label: "Available units", value: s?.available_units },
    { icon: Lock, label: "Held units", value: s?.held_units },
    { icon: Layers, label: "Reserved units", value: s?.reserved_units },
    { icon: PackageX, label: "Out of stock", value: s?.out_of_stock, warn: s?.out_of_stock > 0 },
    { icon: AlertTriangle, label: "Low stock", value: s?.low_stock, warn: s?.low_stock > 0 },
    { icon: DollarSign, label: "Inventory value", value: s?.inventory_value != null ? money(s.inventory_value) : "Restricted", note: "On hand × cost" },
  ];
  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      {s?.products_missing_sku > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm font-bold text-yellow-800 flex items-center gap-2" data-testid="missing-sku-warning">
          <AlertTriangle className="h-4 w-4" /> {s.products_missing_sku} product(s) have no SKU. Every active product needs a unique SKU.
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, label, value, note, warn }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`inv-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between"><Icon className={`h-5 w-5 ${warn ? "text-brand-red" : "text-brand-navy"}`} /><div className="text-xs text-slate-400">{note}</div></div>
            <div className={`mt-3 font-heading text-3xl ${warn ? "text-brand-red" : "text-brand-navy"}`}>{value ?? "—"}</div>
            <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ListCard title="Low stock products" items={s?.low_stock_items} empty="Nothing is low on stock." testId="low-stock-list" onView={() => onViewInventory("low_stock")}
          render={(r) => (<><span className="font-bold truncate">{r.product.product_name}</span><span className="text-slate-500">{r.product.sku}</span><span className="text-brand-red font-bold">{r.inventory.quantity_available} left</span><span className="text-slate-400">reorder @ {r.inventory.reorder_level}</span><span className="text-slate-400">{r.location_name}</span></>)} />
        <ListCard title="Out of stock products" items={s?.out_of_stock_items} empty="Everything is in stock." testId="out-of-stock-list" onView={() => onViewInventory("out_of_stock")}
          render={(r) => (<><span className="font-bold truncate">{r.product.product_name}</span><span className="text-slate-500">{r.product.sku}</span><StatusPill status={r.inventory.inventory_status} /><span className="text-slate-400">{r.location_name}</span></>)} />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="recent-activity">
        <div className="font-heading text-lg text-brand-navy mb-3">Recent inventory activity</div>
        {!s ? <div className="text-slate-400 text-sm">Loading...</div> : s.recent_activity.length === 0 ? <div className="text-slate-400 text-sm">No activity yet.</div> : (
          <div className="divide-y divide-slate-100">
            {s.recent_activity.map((t) => (
              <div key={t.transaction_id} className="py-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm items-center">
                <span className="font-bold truncate">{t.product_name}</span><span className="text-slate-500">{t.sku}</span>
                <span><span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-slate-100 mr-1">{t.transaction_type.replace(/_/g, " ")}</span><b className={t.quantity_change < 0 ? "text-brand-red" : "text-emerald-600"}>{t.quantity_change > 0 ? "+" : ""}{t.quantity_change}</b></span>
                <span className="text-slate-600">{t.user_name}</span><span className="text-slate-400 text-xs">{fmtDate(t.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ListCard = ({ title, items, empty, render, onView, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
    <div className="flex justify-between items-center mb-3">
      <div className="font-heading text-lg text-brand-navy">{title}</div>
      <button onClick={onView} className="text-xs font-black uppercase tracking-wider text-brand-red hover:underline" data-testid={`${testId}-view-btn`}>View inventory →</button>
    </div>
    {!items ? <div className="text-slate-400 text-sm">Loading...</div> : items.length === 0 ? <div className="text-slate-400 text-sm">{empty}</div> : (
      <div className="divide-y divide-slate-100">{items.map((r) => <div key={r.inventory.inventory_id} className="py-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm items-center">{render(r)}</div>)}</div>
    )}
  </div>
);
